import { Column, TableRow, TableColumn } from '@devexpress/dx-react-grid';
import { TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { ReactNode, createContext } from 'react';

export interface GovernanceTag {
  name: string;
  value: string;
}

export enum ResourceFilter {
  ALL = 'all',
  TAGGED = 'tagged',
  UNTAGGED = 'untagged',
}

export interface ResourceData {
  id: string;
  resourceCost: number;
  resourceName: string;
  resourceId: string;
  service: string;
  region: string;
  linkedAccount: string;
  tags: Record<string, string>;
  governanceTags: GovernanceTag[];
  lastUpdate: string;
}

export interface MetricsData {
  coveragePercentage: number;
  coveragePercentageThreshold: number;
  tagCoverage: {
    numerator: {
      money: number;
      count: number;
    };
    denominator: {
      money: number;
      count: number;
    };
  };
  untagged30d: {
    money: number;
    count: number;
  };
  untaggedAnnual: {
    money: number;
    count: number;
  };
}

export type GovernanceTagDistinctValues = Record<string, string[]>;

export interface TagSearchParams {
  tagKey?: string;
  searchStr?: string;
}

export interface GovernanceTagParams {
  uuid?: string;
  resourceId?: string;
  resourceIds?: string[];
  name: string;
  createdBy: string;
  creationDate?: string;
  governanceTags: GovernanceTag[];
}

export interface BulkDeleteResult {
  entityId: string;
  success: boolean;
  error?: string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface ColumnWidthInfo {
  columnName: string;
  width: number | string;
}

export interface ExtendedGovernanceTag extends GovernanceTag {
  selectedValues?: string[];
}

export enum DropdownType {
  KEY = 'key',
  VALUE = 'value',
}

export enum TagField {
  NAME = 'name',
  VALUE = 'value',
}

export interface Tag {
  name: string;
  value: string;
}
export interface FormatterProps {
  value?: string | { [key: string]: string };
  row?: ResourceData;
}

export interface CustomHeaderCellProps extends TableHeaderRow.CellProps {
  column: Column & {
    headerClassName?: string;
  };
  tableRow: TableRow;
  tableColumn: TableColumn;
}

export interface CustomFilterRowProps {
  children?: ReactNode;
  tableRow: TableRow;
}

export interface FilterParams {
  service?: string[];
  region?: string[];
  resourceName?: string[];
  resourceId?: string[];
  linkedAccountId?: string[];
  tags?: string[];
  governanceTags?: string[];
}

export interface TableHeaderProps {
  setters: {
    setShowTagged: (show: boolean) => void;
    setShowUntagged: (show: boolean) => void;
    setFiltersOpen: (open: boolean) => void;
    setShowFilters: (show: boolean) => void;
    setItemsPerPage: (option: { label: string; value: number }) => void;
    setOpenRowsDropdown: (open: boolean) => void;
    setCurrentPage: (page: number) => void;
  };
  flags: {
    showTagged: boolean;
    showUntagged: boolean;
    filtersOpen: boolean;
    showFilters: boolean;
    openRowsDropdown: boolean;
    isLoadingResources: boolean;
  };
  config: {
    filteredDataLength: number;
    totalResourcesLength: number;
    itemsPerPage: { label: string; value: number };
    rowsOptions: Array<{ label: string; value: number }>;
    filteredData: ResourceData[];
  };
  pagination: {
    currentPage: number;
    totalPages?: number;
  };
  handleDownloadCSV: () => void;
  activeFiltersCount?: number;
}

export interface GovernanceTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: ResourceData | null;
  onSaveTags: (params: GovernanceTagParams) => Promise<void>;
  onDeleteTags: (resourceId: string) => Promise<void>;
}

export interface TagAutocompleteDropdownProps {
  label: string;
  options: string[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onSelect?: (value: string | string[], isNew: boolean) => void;
  dropdownType: DropdownType;
  showCreateOption?: boolean;
  onCreateButtonClick?: () => void;
  disabled?: boolean;
}

export interface UseTagManagementProps {
  resource: ResourceData | null;
  isOpen: boolean;
  onSaveTags: (params: GovernanceTagParams) => Promise<void>;
  onDeleteTags: (resourceId: string) => Promise<void>;
  onClose: () => void;
}

export interface PendingTagUpdate {
  newTags: ExtendedGovernanceTag[];
  resetSelection?: boolean;
}

export interface PendingSelectionUpdate {
  selectedIndices: Set<number>;
  selectAll: boolean;
}

export interface FilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface TagsPopoverProps {
  tags: Array<{ name: string; value: string }>;
  children: ReactNode;
}

export interface GovernanceTagsProviderProps {
  children: ReactNode;
}

export interface GovernanceTagsContextType {
  isFiltersChanged: boolean;
  governanceTagsFilters: { [key: string]: FilterOption[] };
  setGovernanceTagsFilters: (filters: { [key: string]: FilterOption[] }) => void;
  hiddenColumnNames: string[];
  setHiddenColumnNames: (columns: string[]) => void;
  showCustomColumnsNames: string[];
  setShowCustomColumnsNames: (columns: string[]) => void;
  metrics: MetricsData | null;
  setMetrics: (metrics: MetricsData | null) => void;
  distinctTagValues: GovernanceTagDistinctValues | null;
  setDistinctTagValues: (values: GovernanceTagDistinctValues | null) => void;
  addNewTagKey: (key: string) => void;
  addNewTagValue: (value: string) => void;
  addNewTagKeyValue: (key: string, value: string) => void;
  filterParams: FilterParams;
  setFilterParams: (params: FilterParams) => void;
  getActiveFiltersCount: () => number;
}

export const GovernanceTagsContext = createContext<GovernanceTagsContextType | undefined>(undefined);

export interface SortParams {
  columnName: string;
  direction: 'asc' | 'desc';
}

export interface ResourceFilterParams {
  searchText?: string;
  service?: string[];
  region?: string[];
  resourceName?: string[];
  resourceId?: string[];
  linkedAccountId?: string[];
  tags?: string[];
  governanceTags?: string[];
}

export interface ResourcePaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: SortParams[];
}

export interface FlatResourcesQueryParams extends ResourceFilterParams, ResourcePaginationParams {
  filter?: ResourceFilter;
}

export interface PaginatedResourcesResponse {
  resources: ResourceData[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
